import {
  LEAD_DEAL_DONE_ID,
  LEAD_VIEWING_ARRANGE_ID,
  LEAD_VIEWING_DONE_ID,
} from "../../constants/leads";
import {
  Button,
  Chip,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { EDIT_LEAD_ROUTE } from "../../constants/routes";
import { useStatusQuery } from "../../redux/api/leadsApi";
import dayjs from "dayjs";
import { isDisabled } from "../../helper/agentRights";
import { selectCurrentAgent } from "../../redux/features/authSlice";
import { useSelector } from "react-redux";
import StatusDescription from "../Leads/status/StatusDescription";
import { TASK_STATUSES } from "../../constants/task";

export const COLUMNS = (refetch) => [
  // {
  //   accessorKey: "leadSerialNo",
  //   header: "S.No",
  //   size: 30,
  // },
  // {
  //   accessorKey: "lastUpdatedDate",
  //   header: "Updated Date",
  //   size: 20,
  //   Cell: ({ row }) => (
  //     <div>{new Date(row.original.leadUpdatedDate).toLocaleDateString()}</div>
  //   ),
  // },

  {
    accessorKey: "leadTag",
    header: "Lead Ref No",
    size: 20,
    Cell: ({ row }) => {
      const currentAgent = useSelector(selectCurrentAgent);
      const leadId = row.original?.leadid;

      // If no leadId, show nothing (keeps row height consistent)
      if (!leadId) return <div style={{ minHeight: 24 }} />;

      const getLeadIdBackgroundColor = (leadTime) => {
        const now = dayjs();
        const leadDate = dayjs(leadTime);
        const diffInHours = now.diff(leadDate, "hour");

        if (diffInHours < 24) return "";
        else if (diffInHours >= 24 && diffInHours < 48) return "orange";
        else return "red";
      };

      const backgroundColor = getLeadIdBackgroundColor(
        row.original?.followupdate
      );

      // Build safe label (avoid showing undefined)
      const tagPrefix = currentAgent?.agcRefTag
        ? `${currentAgent.agcRefTag}-`
        : "";
      const label = `${tagPrefix}L-${leadId}`;

      return (
        <Chip
          label={label}
          size="small"
          sx={{
            cursor: "pointer",
            fontSize: 12,
            backgroundColor: backgroundColor || "#56C662",
            color: "white",
          }}
        />
      );
    },
  },

  {
    accessorKey: "leadUpdatedDate",
    header: "Updated Date",
    size: 10,
    Cell: ({ row }) => {
      const value = row.original.leadUpdatedDate;
      if (!value) return null;

      const date = new Date(value);
      const dateStr = date.toLocaleDateString();
      const timeStr = date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });

      return (
        <div
          style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}
        >
          <span>{dateStr}</span>
          <span style={{ fontSize: "0.8rem", color: "gray" }}>{timeStr}</span>
        </div>
      );
    },
  },

  {
    accessorKey: "followupdate",
    header: "Follow up Date",
    size: 10,
    Cell: ({ row }) => {
      const value = row.original.followupdate;
      if (!value) return "Not Scheduled";

      const date = new Date(value);
      const dateStr = date.toLocaleDateString();
      const timeStr = date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });

      return (
        <div
          style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}
        >
          <span>{dateStr}</span>
          <span style={{ fontSize: "0.8rem", color: "gray" }}>{timeStr}</span>
        </div>
      );
    },
  },

  {
    accessorKey: "firstname", // keep this for filtering
    header: "Full Name",
    size: 30,
    Cell: ({ row }) => {
      const first = row.original.firstname || "";
      const last = row.original.lastname || "";
      const fullName = `${first} ${last}`.trim();

      return (
        <div
          style={{
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2, // ✅ limit to 2 lines max
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "normal",
            wordBreak: "break-word",
            lineHeight: 1.3,
          }}
        >
          {fullName || "—"}
        </div>
      );
    },
    filterFn: "includesString", // ensures filtering still uses firstname
  },

  {
    accessorKey: "userphone",
    header: "Phone",
    size: 20,
    Cell: ({ row }) => (
      <a
        href={`tel:${row.original.userphone}`}
        style={{ textDecoration: "none", color: "inherit" }}
      >
        {row.original.userphone}
      </a>
    ),
  },
  {
    accessorKey: "history",
    header: "History",
    size: 20,
    Cell: ({ row }) => {
      const currentPath = window.location.pathname;
      const segments = currentPath.split("/");
      return (
        <Link
          className="group relative inline-flex items-center overflow-hidden rounded bg-green px-4 py-1 text-white focus:outline-none active:bg-black"
          to={`/${row.original?.userid}/history/${row.original.leadid}`}
        >
          <span className="absolute -start-full transition-all group-hover:start-4">
            <svg
              className="size-3 rtl:rotate-180"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </span>

          <span className="text-sm font-medium transition-all group-hover:ms-4">
            {" "}
            History{" "}
          </span>
        </Link>
      );
    },
  },
  {
    accessorKey: "leadStatus",
    header: "Status",
    size: 25,
    Cell: ({ row }) => {
      const [status, setStatus] = useState(
        row.original.leadStatus?.toString() || ""
      );
      const [updatedStatus, setUpdatedStatus] = useState(status);
      const [updatedStatusName, setUpdatedStatusName] = useState(status);
      const [showComponent, setShowComponent] = useState(false);
      const navigate = useNavigate();
      const { data: STATUS = [] } = useStatusQuery();

      const handleChange = (event) => {
        const newStatus = event.target.value;
        setUpdatedStatus(newStatus);
        setShowComponent(true);
        setUpdatedStatusName(
          STATUS.find((item) => item.statusId === newStatus)?.statusName || ""
        );
        const currentPath = window.location.pathname;
        let newPath;
        if (newStatus === LEAD_VIEWING_ARRANGE_ID) {
          newPath = `${currentPath}/view/${row.original.userid}/viewingarranged/${row.original.leadid}`;
        }
        if (newStatus === LEAD_VIEWING_DONE_ID) {
          newPath = `${currentPath}/view/${row.original.userid}/viewdone/${row.original.leadid}`;
        }
        if (newStatus === LEAD_DEAL_DONE_ID) {
          newPath = `${currentPath}/view/${row.original.userid}/deal/${row.original.leadid}`;
        }
        navigate(newPath);
      };

      return (
        <>
          <TextField
            variant="outlined"
            size="small"
            select
            value={status}
            onChange={handleChange}
            SelectProps={{
              displayEmpty: true,
              MenuProps: {
                sx: { maxHeight: "380px" },
                PaperProps: {
                  sx: {
                    maxWidth: 180, // limit dropdown width
                    "& .MuiMenuItem-root": {
                      whiteSpace: "normal", // ✅ allow wrapping
                      wordBreak: "break-word", // ✅ break long words
                      lineHeight: 1.3,
                      py: 0.8,
                    },
                  },
                },
              },
              renderValue: () => {
                const selectedStatus = STATUS.find(
                  (option) => option.statusId.toString() === status?.toString()
                );
                return selectedStatus ? (
                  <span
                    style={{
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      lineHeight: 1.2,
                      display: "inline-block",
                    }}
                  >
                    {selectedStatus.statusName}
                  </span>
                ) : (
                  <span>Status</span>
                );
              },
            }}
            sx={{ width: "180px" }} // ✅ smaller width now works well
          >
            {STATUS?.map((option) => (
              <MenuItem
                key={option.statusId}
                value={option.statusId}
                sx={{
                  whiteSpace: "normal", // ✅ wrap text into 2–3 lines
                  wordBreak: "break-word",
                  lineHeight: 1.3,
                }}
              >
                {option.statusName}
              </MenuItem>
            ))}
          </TextField>

          {showComponent && (
            <div className="fixed top-0 left-0 xl:top-5 xl:left-20 flex items-center justify-center w-full h-[100vh] z-10 bg-customGray">
              <div className="w-auto sm:w-[600px] h-[350px] bg-white rounded-xl flex flex-col justify-center items-center">
                <StatusDescription
                  setShowVerify={setShowComponent}
                  selectedLeadStatus={updatedStatus}
                  selectedLeadStatusName={updatedStatusName}
                  leadData={row.original}
                  setStatus={setStatus}
                  refetch={refetch} // ✅ pass refetch properly
                />
              </div>
            </div>
          )}
        </>
      );
    },
  },
  {
    header: "Note",
    accessorKey: "note",
    Cell: ({ row }) => {
      const note = row.original.note || "—";

      return (
        <Tooltip title={note} placement="top">
          <div
            style={{
              width: "220px",
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2, // ✅ limits text to 2 lines
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "normal",
              lineHeight: 1.3,
            }}
          >
            {note}
          </div>
        </Tooltip>
      );
    },
    size: 30,
  },
  {
    accessorKey: "whatsappNo",
    header: "Whatsapp No",
    size: 20,
    Cell: ({ row }) => (
      <a
        href={`https://wa.me/${row.original.whatsappNo}`}
        style={{ textDecoration: "none", color: "inherit" }}
      >
        {row.original.whatsappNo}
      </a>
    ),
  },
  {
    accessorKey: "additionalNo",
    header: "Additional No",
    size: 20,
    Cell: ({ row }) => (
      <a
        href={`tel:${row.original.additionalNo}`}
        style={{ textDecoration: "none", color: "inherit" }}
      >
        {row.original.additionalNo}
      </a>
    ),
  },
  {
    accessorKey: "budget",
    header: "Budget",
    size: 20,
  },

  {
    accessorKey: "priorityName",
    header: "Priority Name",
    size: 20,
  },
  {
    accessorKey: "societyName",
    header: "Society Name",
    size: 20,
  },
  {
    accessorKey: "fileNo",
    header: "File No",
    size: 20,
  },
  {
    accessorKey: "block",
    header: "Block",
    size: 20,
  },
  {
    accessorKey: "plotNo",
    header: "Plot No",
    size: 20,
  },
  {
    accessorKey: "plotSize",
    header: "Plot Size",
    size: 20,
  },
  {
    accessorKey: "type",
    header: "Type",
    size: 20,
  },

  {
    accessorKey: "membershipId",
    header: "Membership Id",
    size: 20,
  },
  {
    accessorKey: "useremail",
    header: "Email",
    size: 20,
  },
  {
    accessorKey: "companyName",
    header: "Company Name",
    size: 20,
  },
  {
    accessorKey: "location",
    header: "Location",
    size: 20,
  },
  {
    accessorKey: "designation",
    header: "Designation",
    size: 20,
  },

  {
    accessorKey: "LeadTypeName",
    header: "Lead Type",
    size: 20,
  },

  {
    accessorKey: "MediumName",
    header: "Medium Name",
    size: 20,
  },
  {
    accessorKey: "SourceName",
    header: "Source Type",
    size: 20,
  },

  {
    accessorKey: "Description",
    header: "Description",
    size: 10,
    Cell: ({ row }) => (
      <div>
        <Tooltip title={row.original.note} placement="top">
          <div
            style={{
              whiteSpace: "nowrap",
              width: "250px",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {row.original?.description}
          </div>
        </Tooltip>
      </div>
    ),
  },

  {
    accessorKey: "leadTime",
    header: "Date",
    size: 20,
    Cell: ({ row }) => (
      <div>{new Date(row.original.leadTime).toLocaleDateString()}</div>
    ),
  },

  {
    accessorKey: "propertyrefnum",
    header: "Property Ref Num",
    size: 20,
  },

  {
    accessorKey: "propertydata",
    header: "Property",
    size: 15,
    Cell: ({ row }) => {
      const { id } = useParams();
      return row.original.propertyid === null ? (
        <span>Disable</span>
      ) : (
        <Link
          className="group relative inline-flex items-center overflow-hidden rounded bg-green px-4 py-1 text-white focus:outline-none active:bg-black"
          to={`/leads/view/${row.original?.userid}/details/${row.original.leadid}`}
        >
          <span className="absolute -start-full transition-all group-hover:start-4">
            <svg
              className="size-3 rtl:rotate-180"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </span>
          <span className="text-sm font-medium transition-all group-hover:ms-4">
            Details
          </span>
        </Link>
      );
    },
  },
  {
    accessorKey: "edit",
    header: "Edit",
    size: 15,
    Cell: ({ row }) => {
      const currentAgent = useSelector(selectCurrentAgent);
      const agentRights = currentAgent.agentrights || {};
      const canUpdateLead = !isDisabled(agentRights, "Update Lead");

      return canUpdateLead ? (
        <Link
          className="group relative inline-flex items-center overflow-hidden rounded bg-green px-4 py-1 text-white focus:outline-none active:bg-black"
          to={EDIT_LEAD_ROUTE + row.original.leadid}
        >
          <span className="absolute -start-full transition-all group-hover:start-4">
            <svg
              className="size-3 rtl:rotate-180"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </span>
          <span className="text-sm font-medium transition-all group-hover:ms-4">
            Edit
          </span>
        </Link>
      ) : (
        <Tooltip title="You don't have permission to edit leads">
          <span className="inline-flex items-center rounded bg-gray-400 px-4 py-1 text-white text-sm cursor-not-allowed opacity-60">
            Edit
          </span>
        </Tooltip>
      );
    },
  },
];

export const TASK_COLUMN = (handleEditClick) => [
  {
    accessorKey: "taskId",
    header: "Ref No",
    size: 20,
  },
  {
    header: "Description",
    accessorKey: "description",
    Cell: ({ row }) => (
      <div>
        <Tooltip title={row.original.description} placement="top">
          <div
            style={{
              width: "150px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {row.original.description}
          </div>
        </Tooltip>
      </div>
    ),
    size: 30,
  },
  {
    header: "Assigned By",
    size: 20,
    Cell: ({ row }) => {
      const { senderAgentName } = row.original;
      return senderAgentName ? senderAgentName : "Agency";
    },
  },
  {
    accessorKey: "receiverAgentName",
    header: "Assigned To",
    size: 20,
  },
  {
    accessorKey: "status",
    header: "Status",
    size: 20,
    Cell: ({ cell }) => {
      const statusValue = cell.getValue();
      const statusObj = TASK_STATUSES.find(
        (status) => status.value === statusValue
      );
      return <span>{statusObj?.label ?? "Unknown"}</span>;
    },
  },
  {
    accessorKey: "taskReceivedDate",
    header: "Assigned Date",
    size: 10,
    Cell: ({ row }) => {
      const value = row.original.taskReceivedDate;
      if (!value) return "Not Scheduled";

      const date = new Date(value);
      const dateStr = date.toLocaleDateString();
      const timeStr = date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });

      return (
        <div
          style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}
        >
          <span>{dateStr}</span>
          <span style={{ fontSize: "0.8rem", color: "gray" }}>{timeStr}</span>
        </div>
      );
    },
  },

  {
    accessorKey: "deadLine",
    header: "Due Date",
    size: 10,
    Cell: ({ row }) => {
      const value = row.original.deadLine;
      if (!value) return "Not Scheduled";

      const date = new Date(value);
      const dateStr = date.toLocaleDateString();
      const timeStr = date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });

      return (
        <div
          style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}
        >
          <span>{dateStr}</span>
          <span style={{ fontSize: "0.8rem", color: "gray" }}>{timeStr}</span>
        </div>
      );
    },
  },

  {
    accessorKey: "deadLine",
    header: "Time Remaining",
    size: 30,
    Cell: ({ row }) => {
      const dueDate = new Date(row.original.deadLine);
      const now = new Date();
      const diff = dueDate - now;

      // Get status label if task is overdue
      const statusValue = row.original.status;
      const statusObj = TASK_STATUSES.find(
        (status) => status.value === statusValue
      );
      const statusLabel = statusObj?.label ?? "Unknown";

      if (diff <= 0) {
        return <span style={{ fontWeight: "bold" }}>{statusLabel}</span>;
      }

      // Convert milliseconds to days, hours, minutes
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      // Format the countdown
      let countdown = "";
      if (days > 0) countdown += `${days}d `;
      if (hours > 0 || days > 0) countdown += `${hours}h `;
      countdown += `${minutes}m`;

      return (
        <span style={{ color: "#52c41a", fontWeight: "bold" }}>
          {countdown}
        </span>
      ); // Always green
    },
  },
  {
    header: "Actions",
    id: "actions",
    size: 20,
    Cell: ({ row }) => (
      <Button
        size="small"
        variant="outlined"
        onClick={(e) => {
          e.stopPropagation(); // Prevents row click event
          handleEditClick(row.original); // Trigger edit
        }}
      >
        Edit
      </Button>
    ),
  },
];
